import { api } from '../../api/client'

export type QueueStatus = 'pending' | 'needs_photos' | 'live' | 'shortlist'
export type QueueRow = {
  id: number; title: string; class_slug: string; status: string; shortlisted: boolean
  challenge: string | null; channel: string | null; submitted_at: string | null
  photo_count: number; full_size_count: number; checks: string[]; member: boolean
  /** A presigned private URL; null when the ride has no photo yet. */
  cover_url: string | null
}
export type Queue = { rows: QueueRow[]; total: number; page: number; page_size: number }

export type RidePhotoRow = {
  id: number; position: number; kind: 'finished' | 'build'; is_cover: boolean; small: boolean
  width: number | null; height: number | null; published: boolean; url: string | null
}
export type RideDetail = {
  id: number; slug: string | null; title: string; status: string; class_slug: string
  model_key: string | null; started_from_text: string; story: string; home_track: string | null
  shortlisted: boolean; decline_reason: string | null; retire_reason: string | null
  merged_into_id: number | null; channel: string | null
  submitted_at: string | null; published_at: string | null
  checks: string[]; member: boolean
  entrant: { id: number; name: string; email: string; town: string | null; region: string | null; instagram: string | null }
  parts: { id: number; type: string; text: string }[]
  photos: RidePhotoRow[]
  challenge: { slug: string; theme: string } | null
  awards: { award_id: number; kind: string; period: string; grant_result: GrantOutcome | null; notified: boolean }[]
}
export type RidePatch = {
  title?: string; story?: string; class_slug?: string; model_key?: string | null
  started_from_text?: string; home_track?: string | null
  parts?: { type: string; text: string }[]
  photos?: { id: number; kind?: 'finished' | 'build' }[]
  cover_photo_id?: number
}
export type DeclineReason = 'not_rc' | 'duplicate' | 'photos_unusable' | 'not_suitable' | 'other'
export type ActionResult = { ok: boolean; status?: string; already?: boolean; trial_started?: boolean; emailed?: boolean; shortlisted?: boolean; into_id?: number; published?: number }

/** Finished photos on a live ride that are not public yet: merged in from a duplicate, or
 *  switched from build to finished after approval. Approve publishes them (no new email). */
export function unpublishedFinished(r: Pick<RideDetail, 'status' | 'photos'>): number {
  if (r.status !== 'live') return 0
  return r.photos.filter((p) => p.kind === 'finished' && !p.published).length
}

export function publishNewLabel(n: number): string {
  return n === 1 ? 'Publish 1 new photo' : `Publish ${n} new photos`
}

const base = (pub: string) => `/api/rides/${encodeURIComponent(pub)}`
const one = (pub: string, id: number) => `${base(pub)}/rides/${id}`

export function queue(pub: string, p: { status?: QueueStatus; q?: string; page?: number } = {}): Promise<Queue> {
  const qs = new URLSearchParams({ status: p.status ?? 'pending' })
  if (p.q?.trim()) qs.set('q', p.q.trim())
  qs.set('page', String(p.page ?? 1))
  return api.get<Queue>(`${base(pub)}/queue?${qs.toString()}`)
}
export const ride = (pub: string, id: number) => api.get<RideDetail>(one(pub, id))
export const patchRide = (pub: string, id: number, body: RidePatch) => api.patch<RideDetail>(one(pub, id), body)
export const approve = (pub: string, id: number) => api.post<ActionResult>(`${one(pub, id)}/approve`)
export const askPhotos = (pub: string, id: number) => api.post<ActionResult>(`${one(pub, id)}/ask-photos`)
export const decline = (pub: string, id: number, reason: DeclineReason, note: string) =>
  api.post<ActionResult>(`${one(pub, id)}/decline`, { reason, note })
export const merge = (pub: string, id: number, intoId: number) => api.post<ActionResult>(`${one(pub, id)}/merge`, { into_id: intoId })
export const shortlist = (pub: string, id: number, on: boolean) => api.post<ActionResult>(`${one(pub, id)}/shortlist`, { on })
export const retire = (pub: string, id: number, reason: string) => api.post<ActionResult>(`${one(pub, id)}/retire`, { reason })
export const restore = (pub: string, id: number) => api.post<ActionResult>(`${one(pub, id)}/restore`)

export const ridesHref = (path: string) => `/editorial/${encodeURIComponent(path)}/readers-rides`
export const rideHref = (path: string, id: number) => `${ridesHref(path)}/${id}`

/** A copy of the list with the item at `i` moved by `d` (-1 up, +1 down); unchanged at the ends. */
export function moveItem<T>(list: T[], i: number, d: -1 | 1): T[] {
  const j = i + d
  if (i < 0 || i >= list.length || j < 0 || j >= list.length) return list.slice()
  const out = list.slice()
  ;[out[i], out[j]] = [out[j], out[i]]
  return out
}

export type Tone = 'ok' | 'warn' | 'bad'
const CHECKS: Record<string, { label: string; tone: Tone }> = {
  small_photos: { label: 'Small photos', tone: 'warn' },
  possible_duplicate: { label: 'Possible duplicate', tone: 'bad' },
  returning_builder: { label: 'Returning builder', tone: 'ok' },
  member: { label: 'Member', tone: 'ok' },
  spam: { label: 'Looks like spam', tone: 'bad' },
}
export const checkChip = (key: string): { label: string; tone: Tone } => CHECKS[key] ?? { label: key, tone: 'warn' }

export const DECLINE_REASONS: { key: DeclineReason; label: string }[] = [
  { key: 'not_rc', label: "This doesn't look like an RC build" },
  { key: 'duplicate', label: 'We already have this ride' },
  { key: 'photos_unusable', label: "We couldn't use the photos" },
  { key: 'not_suitable', label: "It isn't a fit for Readers Rides" },
  { key: 'other', label: 'Something else (say why below)' },
]

export const CLASSES: [string, string][] = [
  ['scale-trail', 'Scale & trail'], ['crawlers', 'Crawlers'], ['monster-trucks', 'Monster trucks'],
  ['short-course', 'Short course'], ['buggies', 'Buggies'], ['on-road-drift', 'On-road & drift'],
  ['vintage', 'Vintage'], ['big-rigs', 'Big rigs'], ['other', 'Something else'],
]
export const classLabel = (slug: string): string => CLASSES.find(([s]) => s === slug)?.[1] ?? slug

export const PART_TYPES = [
  'Chassis', 'Body', 'Motor', 'Speed control', 'Battery', 'Nitro/gas engine', 'Wheels & tires',
  'Shocks', 'Axles', 'Paint', 'Graphics', 'Scale accessories', 'Lights', 'Radio', 'Sound', 'Winch', 'Other',
]

export const STATUS_LABEL: Record<string, string> = {
  pending: 'To review', needs_photos: 'Waiting for photos', live: 'Live', declined: 'Declined', retired: 'Retired', merged: 'Merged',
}

// ---------- Awards (Task 11) ----------

export type AwardKind = 'rotm' | 'challenge' | 'roty'
export type GrantOutcome = 'new' | 'extended' | 'refused'
export type Grant = {
  outcome: GrantOutcome; subscription_id: number | null; new_end: string | null
  auto_renew: boolean; refused_reason: string | null
}
export type SocialChannel = 'instagram' | 'facebook' | 'reddit'
export type AwardResult = {
  /** notified: the winner has had the "won" email (now or earlier); emailed: this call sent it. */
  award_id: number; created: boolean; notified: boolean; emailed: boolean; grant: Grant | null
  kind: AwardKind; period: string; honor: string; social: Record<SocialChannel, string>
}
export type WinnerRow = {
  award_id: number; kind: AwardKind; period: string; honor: string
  ride: { id: number; title: string; slug: string; status: string }
  builder: string; grant_result: GrantOutcome | null; grant: Grant | null
  granted_subscription_id: number | null; notified_at: string | null; created_at: string | null
  social: Record<SocialChannel, string>
}

export const awardRide = (pub: string, id: number, kind: AwardKind, period: string) =>
  api.post<AwardResult>(`${one(pub, id)}/award`, { kind, period })
export const retryGrant = (pub: string, awardId: number) =>
  api.post<AwardResult>(`${base(pub)}/awards/${awardId}/retry-grant`)
export const winners = (pub: string) => api.get<{ rows: WinnerRow[] }>(`${base(pub)}/winners`)
/** A plain link: the browser sends the session cookie and saves the ZIP. */
export const magazinePackageHref = (pub: string, id: number) => `${one(pub, id)}/magazine-package`

export const SOCIAL_CHANNELS: { key: SocialChannel; label: string }[] = [
  { key: 'instagram', label: 'Instagram' }, { key: 'facebook', label: 'Facebook' }, { key: 'reddit', label: 'Reddit' },
]

const monthName = (period: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(period)
  if (!m) return period
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
  return `${d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })} ${m[1]}`
}

/** "Ride of the Month · October 2026", "Ride of the Year 2026", "Rust & Patina challenge winner". */
export function awardLabel(kind: string, period: string, theme?: string | null): string {
  if (kind === 'rotm') return `Ride of the Month · ${monthName(period)}`
  if (kind === 'roty') return `Ride of the Year ${period}`
  if (kind === 'challenge') return `${theme || period} challenge winner`
  return kind
}

/** The period the dialog starts with: this month, this year, or the ride's challenge. */
export function defaultPeriod(kind: AwardKind, today: Date, challengeSlug?: string | null): string {
  const y = today.getFullYear()
  if (kind === 'rotm') return `${y}-${String(today.getMonth() + 1).padStart(2, '0')}`
  if (kind === 'roty') return String(y)
  return challengeSlug ?? ''
}

export const prizeYears = (kind: AwardKind): number => (kind === 'roty' ? 3 : 1)

const longDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** One line on what happened to the membership. */
export function grantLine(grant: Grant | null): { text: string; tone: Tone } {
  if (!grant) return { text: 'No membership was granted.', tone: 'warn' }
  if (grant.outcome === 'new') {
    return { text: `New membership, no charge${grant.new_end ? `. It runs to ${longDate(grant.new_end)}` : ''}.`, tone: 'ok' }
  }
  if (grant.outcome === 'extended') {
    if (!grant.new_end) return { text: 'Added to their membership, which never runs out.', tone: 'ok' }
    const pay = grant.auto_renew ? ` Their next payment moves to ${longDate(grant.new_end)}.` : ''
    return { text: `Added to their membership. It now runs to ${longDate(grant.new_end)}.${pay}`, tone: 'ok' }
  }
  return { text: `Grant after cutover. ${grant.refused_reason ?? 'The membership could not be changed here.'}`, tone: 'bad' }
}

/** What a retry-grant click would do: re-run a refused grant, or send a winner email that never went out. */
export function retryAction(grantResult: GrantOutcome | null | undefined, notified: boolean): 'retry' | 'send' | null {
  if (grantResult === 'refused') return 'retry'
  if ((grantResult === 'new' || grantResult === 'extended') && !notified) return 'send'
  return null
}
export const RETRY_LABEL = { retry: 'Retry grant', send: 'Send winner email' } as const

/** The email line after an award or a retry. */
export function emailLine(r: { notified: boolean; emailed: boolean; created: boolean }): { text: string; tone: Tone } {
  if (r.emailed) return { text: 'Winner email sent, with the share kit.', tone: 'ok' }
  if (r.notified) return { text: 'The winner was already emailed. Nothing was sent again.', tone: 'ok' }
  return { text: 'The winner email did not go out. Check the Emails log, then send it again.', tone: 'warn' }
}

// ---------- Build Challenges (Task 13) ----------

export type ChallengeStatus = 'upcoming' | 'open' | 'closed'
export type Challenge = {
  id: number; slug: string; theme: string; blurb: string; opens_on: string | null; closes_on: string | null
  prize_text: string | null; target_issue: string | null; hero_photo_id: number | null; winner_ride_id: number | null
  status: ChallengeStatus; entry_count: number; total_entries: number; days_left: number | null
}
export type ChallengeInput = {
  slug: string; theme: string; blurb: string; opens_on: string; closes_on: string
  prize_text: string; target_issue: string; hero_photo_id?: number | null
}
export type ChallengePatch = Partial<Omit<ChallengeInput, 'slug'>>
export type WinnerResult = AwardResult & { challenge_id: number; winner_ride_id: number }

const chal = (pub: string, id: number) => `${base(pub)}/challenges/${id}`
export const challenges = (pub: string) => api.get<{ rows: Challenge[] }>(`${base(pub)}/challenges`)
export const createChallenge = (pub: string, body: ChallengeInput) => api.post<Challenge>(`${base(pub)}/challenges`, body)
export const patchChallenge = (pub: string, id: number, body: ChallengePatch) => api.patch<Challenge>(chal(pub, id), body)
export const deleteChallenge = (pub: string, id: number) => api.del<{ ok: boolean }>(chal(pub, id))
export const challengeEntries = (pub: string, id: number) =>
  api.get<{ winner_ride_id: number | null; rows: QueueRow[] }>(`${chal(pub, id)}/entries`)
export const pickWinner = (pub: string, id: number, rideId: number) =>
  api.post<WinnerResult>(`${chal(pub, id)}/winner`, { ride_id: rideId })

/** "rust-patina" from "Rust & Patina": lowercase, dashes, at most 60 characters. */
export const slugify = (theme: string): string =>
  theme.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/, '')
export const SLUG_OK = /^[a-z0-9-]{3,60}$/

export function challengeStatusLine(c: Pick<Challenge, 'status' | 'days_left' | 'winner_ride_id'>): string {
  if (c.winner_ride_id) return 'Winner picked'
  if (c.status === 'upcoming') return 'Opens soon'
  if (c.status === 'closed') return 'Closed, no winner yet'
  if (c.days_left === 0) return 'Closes today'
  return c.days_left === 1 ? '1 day left' : `${c.days_left ?? 0} days left`
}

// ---------- Campaign emails to entrants (Task 14) ----------

export type CampaignKind = 'challenge_launch' | 'challenge_reminder' | 'legacy_reconsent'
export type CampaignRun = {
  id: number; kind: CampaignKind; challenge_id: number | null; challenge_theme: string | null
  status: 'queued' | 'running' | 'done' | 'failed'; auto: boolean
  count: number; sent: number; refused: number; error: string | null
  created_at: string | null; finished_at: string | null
}
export type CampaignsInfo = { can_send: boolean; runs: CampaignRun[]; newsletter_block: string | null }
export type CampaignDryRun = { dry_run: true; count: number; sample: string[]; subject: string }

export const CAMPAIGN_KINDS: { key: CampaignKind; label: string; who: string }[] = [
  { key: 'challenge_launch', label: 'Challenge launch', who: 'Entrants who asked for Readers Rides news' },
  { key: 'challenge_reminder', label: '5 days left reminder', who: 'Entrants who asked for Readers Rides news and have not entered this challenge. It also goes out by itself 5 days before the close.' },
  { key: 'legacy_reconsent', label: "We've rebuilt Readers Rides", who: 'Builders from the old site who never said yes to email. Each gets it once, ever.' },
]
export const campaignNeedsChallenge = (kind: CampaignKind): boolean => kind !== 'legacy_reconsent'

export const campaigns = (pub: string) => api.get<CampaignsInfo>(`${base(pub)}/campaigns`)
export const countCampaign = (pub: string, kind: CampaignKind, challengeId: number | null) =>
  api.post<CampaignDryRun>(`${base(pub)}/campaigns`, { kind, challenge_id: challengeId, dry_run: true })
export const sendCampaign = (pub: string, kind: CampaignKind, challengeId: number | null) =>
  api.post<CampaignRun & { dry_run: false }>(`${base(pub)}/campaigns`, { kind, challenge_id: challengeId, dry_run: false })

const people = (n: number): string => (n === 1 ? '1 person' : `${n} people`)

/** "Sent to 40 of 42 people, 2 not sent" and the like. */
export function campaignRunLine(r: Pick<CampaignRun, 'status' | 'count' | 'sent' | 'refused' | 'error'>): string {
  if (r.status === 'queued') return `Waiting to send to ${people(r.count)}`
  if (r.status === 'running') return `Sending: ${r.sent} of ${r.count} so far`
  if (r.status === 'failed') return `Stopped after ${r.sent} sent. ${r.error ?? ''}`.trim()
  return `Sent to ${people(r.sent)}${r.refused ? `, ${r.refused} not sent (see the Emails log)` : ''}`
}

/** Why a count came back 0. */
export function zeroCountLine(kind: CampaignKind): string {
  if (kind === 'legacy_reconsent') return 'Nobody would get it right now. No builders from the old site are left who have not had it (or they are on the bounce list).'
  return 'Nobody would get it right now. Either nobody has said yes to Readers Rides emails yet, or everyone who has has already had this one or entered the challenge.'
}

export type NumbersMonth = {
  month: string; entries: number; approved: number
  by_channel: Record<string, number>; by_challenge: Record<string, number>
}
export type Numbers = {
  months: NumbersMonth[]; ride_page_sessions: number
  newsletter_signups_from_rides: number; memberships_from_rides: number
}
export const numbers = (pub: string) => api.get<Numbers>(`${base(pub)}/numbers`)

/** "2026-09" -> "Sep 2026". */
export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number)
  const name = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return name ? `${name} ${y}` : key
}
/** "email 3, facebook 1", biggest first; "none" when empty. */
export function breakdownLine(b: Record<string, number>): string {
  const parts = Object.entries(b).sort((a, c) => c[1] - a[1] || a[0].localeCompare(c[0])).map(([k, n]) => `${k} ${n}`)
  return parts.length ? parts.join(', ') : 'none'
}
/** Bar width as a whole percent of the busiest month; 0 when there is nothing to show. */
export const barPct = (n: number, max: number): number => (max > 0 ? Math.round((n / max) * 100) : 0)
